import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, delMeta, pedirBorrarCopia } from '../db/dexie.js';
import { sincronizar } from '../sync/engine.js';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Confirmar } from './Preguntar.js';

/**
 * The server will not take anything from this device any more: the owner revoked it in
 * «Personal y equipos» — a lost phone, somebody who left with it — or the server no longer knows
 * its token. In place of the whole app, because a sale rung up here now would never reach the
 * shop, and the person ringing it would not know.
 *
 * What was never sent stays on the device until somebody chooses to wipe it; wiping is how it
 * gets registered again. Or the owner gives it back in «Personal y equipos» («Volver a dar de
 * alta»), and it carries on with all of it: the sync that keeps trying behind this screen goes
 * through, and the app opens again by itself. «Intentar otra vez» tries at once, for that and for
 * the server that forgot and then remembered — a restore, a mistake put right.
 */
export function DadoDeBaja() {
  const t = useT();
  const pendientes = useLiveQuery(() => db.eventos.where('estado').equals('pendiente').count(), [], 0);
  const [borrando, setBorrando] = useState(false);
  const [probando, setProbando] = useState(false);

  async function intentar() {
    setProbando(true);
    await delMeta('dadoDeBaja');
    await sincronizar();
    setProbando(false);
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-5 p-6 text-center">
      <span className="chip chip-rosa size-16 rounded-2xl">
        <Ico icono="bloquear" className="size-8" />
      </span>
      <h1 className="text-2xl font-bold">{t('baja.titulo')}</h1>
      <p className="text-sm text-slate-400">{t('baja.explicacion')}</p>
      {pendientes > 0 && (
        <p className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {t('baja.pendientes', { n: pendientes })}
        </p>
      )}
      <div className="flex w-full flex-col gap-3">
        <button type="button" className="btn-secundario w-full" disabled={probando} onClick={() => void intentar()}>
          <Ico icono="sincronizar" />
          {t(probando ? 'sync.sincronizando' : 'baja.intentar')}
        </button>
        <button type="button" className="btn-primario w-full" onClick={() => setBorrando(true)}>
          <Ico icono="reiniciar" />
          {t('baja.borrar')}
        </button>
      </div>

      {borrando && (
        <Confirmar
          titulo={t('baja.borrarTitulo')}
          mensaje={t(pendientes > 0 ? 'baja.borrarConPendientes' : 'baja.borrarMensaje', { n: pendientes })}
          confirmar={t('baja.borrar')}
          icono="reiniciar"
          peligro
          onCerrar={() => setBorrando(false)}
          onConfirmar={() => {
            pedirBorrarCopia();
            location.reload();
          }}
        />
      )}
    </div>
  );
}
